// ----------------------------------------------------------------------
// <copyright file="Izenda.SimpleChart.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import ChartOptionsBuilder from './Izenda.ChartOptionsBuilder';
import vizEngineFactories from 'share/factories/Izenda.VizEngineFactories';
import cfgFactories from 'share/factories/Izenda.ReportPartStyleConfigurationFactories';
import { get, debounce } from 'lodash/fp';

@observer
export default class SimpleChart extends Component {
  chart: null;
  constructor() {
    super();
    this.state = {
      chartMsg: '',
      theChart: ''
    };
    this.debounceCompleted = null;
  }

  componentWillReceiveProps() {
    this.setState({ chartMsg: null });
  }

  componentDidUpdate() {
    this.renderChart();
  }

  componentDidMount() {
    this.renderChart();
  }

  componentWillUnmount() {
    this.destroyChart();
  }

  destroyChart() {
    this.chart && this.chart.destroy && this.chart.destroy();
  }

  renderWithFieldName = () => {
    const {chartOptions:{commonOptions}} = this.props;
    const style = this.props.style || {};
    style.height = '88%';
    const {series} = commonOptions;
    const name = (series.hasOwnProperty('serieName') ? series.serieName : series[0]['name'] );
    return (
      <div style={{width:'100%', 'height': '100%'}}>
        <div ref='chart' className='izenda-chart' style={style}>
          {this.state.chartMsg}
        </div>
        <div style={{width:'100%', 'textAlign':'center', verticalAlign:'top', marginTop: '-3px'}}>
          {name}
        </div>
      </div>
    );
  }

  renderWithoutFieldName = () => {
    const style = this.props.style || {};
    return (
      <div ref='chart' className='izenda-chart' style={style}>
        {this.state.chartMsg}
      </div>
    );
  }

  render() {
    const {chartOptions:{optionByType}} = this.props;
    return (optionByType.izShowValueFieldName? this.renderWithFieldName():this.renderWithoutFieldName());
  }

  renderChart() {
    const { finishedRender } = this.props;

    // do not draw chart
    if (this.state.chartMsg) {
      if(typeof finishedRender === 'function') {
        finishedRender();
      }

      return;
    }

    const { type: reportPartType, chartType: reportPartStyle, printingMode, vizEngine: partVizEngine } = this.props;

    try {
      // Get configuration of the report part style
      const { visualEngine, visualType, optionsMapping, optionsBuilder } = cfgFactories.getConfiguration(
        reportPartType,
        reportPartStyle,
        partVizEngine
      );

      // Get visualization engine of the report part style. This viz engine was associated with the particular
      // report part style in its configuration.
      const vizEngine = vizEngineFactories.getVisualizationEngine(visualEngine, reportPartStyle);

      // Build the visualization options,aka configuration in order to guide the visualization to render
      const options = this.buildOptions(optionsMapping, visualType, optionsBuilder);

      // Disable legend animation when printing
      $izenda.extend(true, options, {
        chart: {
          animation: !printingMode
        },
        plotOptions: {
          series: {
            animation: !printingMode
          }
        }
      });

      // Include printing mode to options
      options.printingMode = !!printingMode;

      // Invoke viz engine to execute drawing into chart container
      this.chart = vizEngine.draw(this.refs.chart, visualType, options, this.renderCompleted);
    } catch (e) {
      if(typeof finishedRender === 'function') {
        finishedRender();
      }

      console && console.error(e.message, e);
      this.setState({ chartMsg: 'Can\'t draw chart' });
    }
  }

  /**
   * Constructs the visualization specific options using configured builder
   * @param {Object} optionsMapping Options mapping
   * @param {string} visualType The visualization spec type, e.g. 'line' in Highchart.
   * @param {Function} optionsBuilder The option builder class
   */
  buildOptions(optionsMapping, visualType, optionsBuilder) {
    const { chartOptions, chartData, seriesOptions, onClickXLabel, onClickYLabel } = this.props;
    const Builder = optionsBuilder || ChartOptionsBuilder;
    const builder = new Builder(visualType, this.refs.chart, chartData, chartOptions, seriesOptions);
    builder.mapOptionValues(optionsMapping);
    onClickXLabel && builder.setOnClickXLabel(e => onClickXLabel(e));
    onClickYLabel && builder.setOnClickYLabel(e => onClickYLabel(e));

    return builder.build();
  }

  renderCompleted = (chart: any) => {
    // Remove clipping after animation had been completed to help IE not stuttering when scrolling with sticky
    if (chart && chart.clipRect && chart.clipRect.clipPath) {
      chart.clipRect.clipPath.destroy();
      chart.clipRect.clipPath = null;
    }

    const { finishedRender, fulfilledData, printingMode } = this.props;
    if (finishedRender && fulfilledData) {
      if (!printingMode) {
        finishedRender();
        return;
      }

      const simpleChartContainer = get('[0].renderTo', chart);
      if(simpleChartContainer) {
        $izenda(simpleChartContainer).find('.highcharts-legend-navigation').parent().hide();
      }
      const simpleChart = this;
      if (!simpleChart.printControl) {
        simpleChart.printControl = debounce(500, () => {
          const printCompleted = simpleChart.completed;
          simpleChart.completed = true;
          !printCompleted && finishedRender();
        });
      }
      simpleChart.printControl();
    }
  };
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.SimpleChart.js